import { findInTreeList } from "./tree";
import _ from 'lodash';
import { getItem } from "./storageHelper";



export const getTaskColor = (task, parent) => {
    if (task && task.color) {
        return task.color.hexString;
    } else if (parent && parent.color) {
        return parent.color.hexString;
    } else {
        return "#80C3E9";
    }
};

export const getSectionNames = (todos) => {
    return todos
        .filter((item) => item.type === 1 && !item.isCompleted)
        .map((project) => ({ title: project.title, id: project.id }));
};

export const getTopLevelParentRef = (todo, projects) => {
    let currentTodo = todo;
    let topLevelParentRef = null;

    while (currentTodo.parent) {
        let parent = findInTreeList(
            projects,
            (found) => found.$id === currentTodo.parent.$ref
        );
        if (parent) {
            topLevelParentRef = parent.id;
            currentTodo = parent;
        } else {
            break;
        }
    }

    return topLevelParentRef;
};

export const getPrimaryCalendarId = async () => {
    const calendars = JSON.parse(await getItem("calendar"));
    return calendars.Primary;
};
const normalizeEventsStructure = (existingEventsCopy) => {
    let currentEvents = [];
    if (Array.isArray(existingEventsCopy)) {
        currentEvents = existingEventsCopy;
    } else if (existingEventsCopy && existingEventsCopy.items && Array.isArray(existingEventsCopy.items)) {
        currentEvents = existingEventsCopy.items;
    } else {
        currentEvents = [];
    }
    return currentEvents;
};

// Helper function to return proper structure
const returnEventsStructure = (existingEventsCopy, updatedEvents) => {
    if (Array.isArray(existingEventsCopy)) {
        return updatedEvents;
    } else {
        return {
            ...existingEventsCopy,
            items: updatedEvents
        };
    }
};

// Add a single event
export const addSyncEvent = (existingEventsCopy, newEvent) => {
    if (!newEvent) {
        return existingEventsCopy;
    }

    const currentEvents = normalizeEventsStructure(existingEventsCopy);

    // Check if event already exists
    const existingIndex = currentEvents.findIndex(event => event.id === newEvent.id);

    let updatedEvents;
    if (existingIndex !== -1) {
        // Event already exists, update it instead
        updatedEvents = [...currentEvents];
        updatedEvents[existingIndex] = _.cloneDeep(newEvent);
        console.log(`Event ${newEvent.id} already exists, updated instead of added`);
    } else {
        // Add new event
        updatedEvents = [...currentEvents, _.cloneDeep(newEvent)];
        console.log(`Event ${newEvent.id} added successfully`);
    }

    return returnEventsStructure(existingEventsCopy, updatedEvents);
};

// Update a single event
export const updateSyncEvent = (existingEventsCopy, updatedEvent) => {
    if (!updatedEvent || !updatedEvent.id) {
        console.log('Invalid event data for update');
        return existingEventsCopy;
    }

    const currentEvents = normalizeEventsStructure(existingEventsCopy);
    const existingIndex = currentEvents.findIndex(event => event.id === updatedEvent.id);

    let updatedEvents;
    if (existingIndex !== -1) {
        // Update existing event
        updatedEvents = [...currentEvents];
        updatedEvents[existingIndex] = _.merge(_.cloneDeep(currentEvents[existingIndex]), updatedEvent);
        console.log(`Event ${updatedEvent.id} updated successfully`);
    } else {
        // Event doesn't exist, add it as new
        updatedEvents = [...currentEvents, _.cloneDeep(updatedEvent)];
        console.log(`Event ${updatedEvent.id} not found, added as new event`);
    }

    return returnEventsStructure(existingEventsCopy, updatedEvents);
};

// Delete a single event
export const deleteSyncEvent = (existingEventsCopy, eventIdOrEvent) => {
    if (!eventIdOrEvent) {
        console.log('No event ID provided for deletion');
        return existingEventsCopy;
    }

    const currentEvents = normalizeEventsStructure(existingEventsCopy);

    // Handle both string ID or event object
    const eventId = typeof eventIdOrEvent === 'string' ? eventIdOrEvent : eventIdOrEvent.id;

    if (!eventId) {
        console.log('Invalid event ID for deletion');
        return existingEventsCopy;
    }

    const existingIndex = currentEvents.findIndex(event => event.id === eventId);

    let updatedEvents;
    if (existingIndex !== -1) {
        // Remove the event
        updatedEvents = [...currentEvents];
        updatedEvents.splice(existingIndex, 1);
        console.log(`Event ${eventId} deleted successfully`);
    } else {
        // Event not found
        console.log(`Event ${eventId} not found for deletion`);
        updatedEvents = currentEvents;
    }

    return returnEventsStructure(existingEventsCopy, updatedEvents);
};
export const mergeSyncEvents = (existingEventsCopy, syncItems) => {
    // Extract the items array from the calendar response object
    let currentEvents = [];

    if (Array.isArray(existingEventsCopy)) {
        // If it's already an array, use it directly
        currentEvents = existingEventsCopy;
    } else if (existingEventsCopy && existingEventsCopy.items && Array.isArray(existingEventsCopy.items)) {
        // If it's a calendar response object, extract the items array
        currentEvents = existingEventsCopy.items;
    } else {
        // Fallback to empty array
        currentEvents = [];
    }

    if (!syncItems || !Array.isArray(syncItems)) {
        return {
            ...existingEventsCopy,
            items: currentEvents
        };
    }

    // Work with the events array
    let updatedEvents = [...currentEvents]; // Shallow copy since we already deep copied the parent

    syncItems.forEach(syncItem => {
        const existingIndex = updatedEvents.findIndex(event => event.id === syncItem.id);

        if (syncItem.status === 'cancelled') {
            // Remove cancelled events
            if (existingIndex !== -1) {
                updatedEvents.splice(existingIndex, 1);
            }
        } else {
            // Update existing or add new events
            if (existingIndex !== -1) {
                // Deep merge the sync item with existing event
                updatedEvents[existingIndex] = _.merge(updatedEvents[existingIndex], syncItem);
            } else {
                // Add new event (deep clone to ensure no reference issues)
                updatedEvents.push(_.cloneDeep(syncItem));
            }
        }
    });

    // Return the full calendar response object with updated items
    return {
        ...existingEventsCopy,
        items: updatedEvents
    };
};
// Synchronous version for use in reducers - works on web, iPad, and Android
export const determineEventArrayType = (eventData, calendarMapping = null) => {
    if (!eventData) return 'primary';

    // Get calendarId from different possible locations
    const calendarId = eventData.calendarId ||
        eventData.organizer?.email ||
        eventData.extendedProperties?.[0]?.calendarId ||
        eventData.extendedProperties?.private?.calendarId;

    if (!calendarId) return 'primary';

    // Use calendarMapping from Redux state if provided (works on all platforms)
    if (calendarMapping) {
        // Direct match with calendar IDs from Redux
        if (calendarId === calendarMapping.Primary) return 'primary';
        if (calendarId === calendarMapping.GlobalTasks) return 'gt';
        if (calendarId === calendarMapping.MyTimetable) return 'timetable';
    } else {
        // Fallback: Try localStorage (web only) if calendarMapping not provided
        try {
            const calendarData = localStorage.getItem('calendar');
            if (calendarData) {
                const mapping = JSON.parse(calendarData);
                if (calendarId === mapping.Primary) return 'primary';
                if (calendarId === mapping.GlobalTasks) return 'gt';
                if (calendarId === mapping.MyTimetable) return 'timetable';
            }
        } catch (error) {
            // localStorage not available (native platform without mapping)
            console.warn('Could not read calendar mapping:', error);
        }
    }

    // Final fallback: group calendar = gt, everything else = primary
    return calendarId.includes('group.calendar.google.com') ? 'gt' : 'primary';
};

export const getEventCoordinates = (jsEvent) => {
    let x, y;

    if (jsEvent.type.includes('touch')) {
        // For touch events, use the first touch point
        const touch = jsEvent.touches?.[0] || jsEvent.changedTouches?.[0];
        if (touch) {
            x = touch.pageX;
            y = touch.pageY;
        }
    } else {
        // For mouse events
        x = jsEvent.pageX;
        y = jsEvent.pageY;
    }

    // Fallback to clientX/Y if pageX/Y not available
    if (x === undefined || y === undefined) {
        x = (jsEvent.clientX || 0) + window.scrollX;
        y = (jsEvent.clientY || 0) + window.scrollY;
    }

    return { x, y };
};
